import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Newspaper, ArrowRight, Loader2, AlertCircle, ChevronLeft, ChevronRight, Pin } from 'lucide-react'
import SectionHeading from './SectionHeading'
import { useLanguage } from '@/localization'
import { useNews, formatNewsDate, type NewsItem } from '@/hooks/useNews'

interface NewsStripProps {
  /** Variant B blocky styling. */
  blocky?: boolean
}

/**
 * "Latest Minecraft news" teaser — featured (pinned) slot on top, latest 3
 * below, and a "View all news →" link to /news. If more than one post is
 * pinned the featured slot becomes a hand-rolled carousel; with no pinned
 * posts the most-recent post fills the featured slot. Cards link to /news/:id.
 */
export default function NewsStrip({ blocky = false }: NewsStripProps) {
  const { t } = useLanguage()
  const state = useNews()

  const cardShape = blocky ? 'block-bevel rounded-none bg-white' : 'rounded-xl border shadow-block bg-white'

  return (
    <section id="news" className="scroll-mt-20 mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
      <SectionHeading eyebrow="Latest updates" title="Minecraft News" />

      {state.status === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-8 text-slate-deep/60">
          <Loader2 className="animate-spin" size={20} />
          {t('Loading the latest updates…')}
        </div>
      )}

      {state.status === 'error' && (
        <div className={['mx-auto flex max-w-lg items-center gap-3 p-5 text-slate-deep/70 border-stone-light/60', cardShape].join(' ')}>
          <AlertCircle className="text-gold-dark shrink-0" size={22} />
          <span>{t('News is taking a break right now — check back soon for the latest Minecraft updates.')}</span>
        </div>
      )}

      {state.status === 'empty' && (
        <div className={['mx-auto flex max-w-lg items-center gap-3 p-5 text-slate-deep/70 border-stone-light/60', cardShape].join(' ')}>
          <Newspaper className="text-grass-dark shrink-0" size={22} />
          <span>{t('No news yet — check back here for vanilla updates and community events.')}</span>
        </div>
      )}

      {state.status === 'ready' && <NewsContent items={state.items} cardShape={cardShape} />}
    </section>
  )
}

function NewsContent({ items, cardShape }: { items: NewsItem[]; cardShape: string }) {
  const { t } = useLanguage()
  const pinned = items.filter((i) => i.pinned)
  // Featured = pinned posts; if none pinned, the most-recent post fills it.
  const featured = pinned.length ? pinned : items.slice(0, 1)
  const featuredIds = new Set(featured.map((i) => i.id))
  const latest = items.filter((i) => !featuredIds.has(i.id)).slice(0, 3)

  return (
    <div className="space-y-8">
      <FeaturedSlot featured={featured} cardShape={cardShape} />

      {latest.length > 0 && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {latest.map((item) => (
            <NewsCard key={item.id} item={item} cardShape={cardShape} />
          ))}
        </div>
      )}

      <div className="text-center">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 font-bold text-grass-deep transition-colors hover:text-grass-dark"
        >
          {t('View all news')} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  )
}

/** Featured slot — single card, or a hand-rolled carousel if >1 pinned. */
function FeaturedSlot({ featured, cardShape }: { featured: NewsItem[]; cardShape: string }) {
  const { t } = useLanguage()
  const [index, setIndex] = useState(0)
  if (!featured.length) return null

  const safeIndex = index % featured.length
  const item = featured[safeIndex]
  const isCarousel = featured.length > 1

  return (
    <div className="relative">
      <FeaturedCard item={item} cardShape={cardShape} />

      {isCarousel && (
        <>
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + featured.length) % featured.length)}
            aria-label={t('Previous featured post')}
            className="absolute left-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-deep/80 text-white transition-colors hover:bg-grass-dark cursor-pointer"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % featured.length)}
            aria-label={t('Next featured post')}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-deep/80 text-white transition-colors hover:bg-grass-dark cursor-pointer"
          >
            <ChevronRight size={20} />
          </button>
          <div className="mt-4 flex justify-center gap-2">
            {featured.map((f, i) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`${t('Go to featured post')} ${i + 1}`}
                className={[
                  'h-2.5 rounded-full transition-all cursor-pointer',
                  i === safeIndex ? 'w-6 bg-grass-dark' : 'w-2.5 bg-stone-light',
                ].join(' ')}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function FeaturedCard({ item, cardShape }: { item: NewsItem; cardShape: string }) {
  const { t } = useLanguage()
  const date = formatNewsDate(item.date)
  return (
    <Link
      to={`/news/${item.id}`}
      className={[
        'group flex flex-col gap-3 p-6 sm:p-8 border-stone-light/60 transition-colors hover:border-grass-dark/60',
        cardShape,
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1 rounded-md bg-gold/20 px-2.5 py-1 font-pixel text-[9px] uppercase tracking-[0.15em] text-gold-dark">
          <Pin size={11} /> {t('Featured')}
        </span>
        {date && <span className="text-xs font-bold text-grass-deep">{date}</span>}
      </div>
      <h3 className="text-2xl font-extrabold text-slate-deep transition-colors group-hover:text-grass-deep sm:text-3xl">
        {item.title}
      </h3>
      {item.excerpt && <p className="max-w-3xl text-slate-deep/70">{item.excerpt}</p>}
      <span className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-grass-deep">
        {t('Read more')} <ArrowRight size={14} />
      </span>
    </Link>
  )
}

function NewsCard({ item, cardShape }: { item: NewsItem; cardShape: string }) {
  const { t } = useLanguage()
  const date = formatNewsDate(item.date)
  return (
    <Link
      to={`/news/${item.id}`}
      className={[
        'group flex flex-col p-5 border-stone-light/60 transition-colors hover:border-grass-dark/60',
        cardShape,
      ].join(' ')}
    >
      {date && <span className="mb-2 text-xs font-bold text-grass-deep">{date}</span>}
      <h3 className="mb-2 font-extrabold text-slate-deep transition-colors group-hover:text-grass-deep">
        {item.title}
      </h3>
      {item.excerpt && <p className="line-clamp-3 flex-1 text-sm text-slate-deep/70">{item.excerpt}</p>}
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-grass-deep">
        {t('Read more')} <ArrowRight size={12} />
      </span>
    </Link>
  )
}
